import { PrismicNextImage, PrismicNextImageProps } from '@prismicio/next';
import { isFilled } from '@prismicio/client';
import { ImageField } from '@prismicio/client';
import { IconSvg } from '@/components';
import { ReactNode } from 'react';

/**
 * Ensures that a Prismic image has accessible alt text.
 * If alt is empty, inject a fallback.
 *
 * @param field - Image object from Prismic
 * @param defaultAlt - Default alternative text if not from Prismic
 * @param showPlaceholderIfEmpty - In case you need to render a placeholder
 * @returns - Renders the prismic image component with alt accessibility in case an image comes in
 */

type PrismicImageWithAltProps = Omit<PrismicNextImageProps, 'field'> & {
  field: ImageField | null | undefined;
  defaultAlt?: string;
  showPlaceholderIfEmpty?: boolean;
  noImageContent?: ReactNode;
};

const PrismicImageWithAlt = ({
  field,
  defaultAlt = 'Image',
  showPlaceholderIfEmpty = false,
  noImageContent = <IconSvg name="image" />,

  ...props
}: PrismicImageWithAltProps) => {
  if (!isFilled?.image(field)) {
    return showPlaceholderIfEmpty ? <>{noImageContent}</> : null;
  }

  const safeField = {
    ...field,
    alt: field.alt?.trim() ? field.alt : defaultAlt,
  } as ImageField;

  return <PrismicNextImage field={safeField} {...props} fallbackAlt="" />;
};

export default PrismicImageWithAlt;
